import { isSessionPatientLinkingReadOnly } from '@/hooks/integrations/isSessionPatientLinkingReadOnly';
import { useMutateUnlinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateUnlinkSessionsToPatientProfile';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useFormatSessionTime } from '@/hooks/useFormatSessionTime';
import { cn } from '@/lib/cn';
import { type SimpleSessionViewV2 } from '@/types';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyFootNote } from '@heidi/design-system/typography';
import { type PatientProfile } from '@heidi/patients/contracts';
import { startTracking, TrackingKey } from '@heidi/runtime/performance';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { PatientSource } from '@heidi/schema/ml-scribe';
import { Unlink } from 'lucide-react';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import style from '../accordion-nav-button.module.scss';

export const hiddenClassName =
  'text-text-primary-invertopacity-0 transition-all duration-300 -z-10 group-hover:z-50 group-hover:ml-4 group-hover:rounded group-hover:bg-background-primary group-hover:p-2 group-hover:opacity-100 h-full flex items-center';

type LinkedSessionListProps = {
  session: SimpleSessionViewV2;
  onLinkedSessionSelected: (value: string) => void;
  isSelected: boolean;
  patientProfile?: PatientProfile;
  isEHRPatientProfile: boolean;
  isPatientLinkingReadOnly: boolean;
};

const LinkedSession = ({
  session,
  onLinkedSessionSelected,
  isSelected,
  patientProfile,
  isEHRPatientProfile,
  isPatientLinkingReadOnly,
}: LinkedSessionListProps) => {
  const { mutate: unlinkSessionsToPatientProfile } =
    useMutateUnlinkSessionsToPatientProfile();
  const currentSessionId = useCurrentSessionId();
  const { data: userData } = useQueryUser();
  const formatSessionTime = useFormatSessionTime(
    patientProfile?.source !== PatientSource.EHR
  );

  const isUnlinkDisabled =
    isPatientLinkingReadOnly ||
    isSessionPatientLinkingReadOnly({ ehr_appt_id: session.ehr_appt_id });

  const onSessionUnlinkClick = () => {
    if (patientProfile && !isUnlinkDisabled) {
      unlinkSessionsToPatientProfile({
        sessionIds: [session.session_id],
        patientProfile: patientProfile,
        sessionId: currentSessionId || undefined,
      });
    }
  };

  const router = useRouter();

  const onLinkedSessionClicked = (sessionId: string) => {
    startTracking({
      key: TrackingKey.SCRIBE_SESSION_OPEN,
      metadata: { session_id: sessionId },
    });
    void router.push(`/scribe/session/${sessionId}`);
  };
  return (
    <div
      className={cn(
        'group relative flex items-center justify-between gap-x-3 rounded-sm bg-surface',
        'cursor-pointer hover:bg-background-secondary',
        !isEHRPatientProfile && style['sidebar-link'],
        isSelected && style['selected'],
        isSelected && 'bg-background-secondary'
      )}
    >
      <div
        className={cn(
          'absolute start-[0.9rem] flex h-full flex-row items-center opacity-0',
          style['checkbox-wrapper']
        )}
      >
        <Checkbox
          checked={isSelected}
          onClick={(e) => {
            // use onClick instead of onCheckedChange to prevent the click event from clicking the link
            e.preventDefault();
            onLinkedSessionSelected(session.session_id);
          }}
        />
      </div>
      <Button
        disableAutomaticSvgSizing
        variant={'ghost'}
        size="touch"
        className="flex !h-fit w-full items-start justify-start gap-0.5 p-2"
        onClick={() => {
          onLinkedSessionClicked(session.session_id);
        }}
      >
        <div className="flex w-full min-w-0 flex-col">
          <TypographyCaption className="text-left">
            {formatSessionTime(
              session.created_time_local || session.created_at || '',
              userData?.session_settings?.date_format || undefined
            )}
          </TypographyCaption>
          {session.session_gist && (
            <TypographyFootNote className="w-full truncate text-left text-text-secondary">
              {session.session_gist}
            </TypographyFootNote>
          )}
        </div>
      </Button>
      <div className="invisible flex h-full items-center gap-1.5 pr-2 group-hover:visible">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild className="flex cursor-default items-center">
              <button
                type="button"
                disabled={isUnlinkDisabled}
                className={cn(
                  'flex size-4 items-center justify-center rounded-sm',
                  isUnlinkDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-default hover:bg-background-secondary-hover'
                )}
              >
                <Unlink
                  size={14}
                  className="text-text-secondary"
                  onClick={(e) => {
                    if (isUnlinkDisabled) {
                      e.preventDefault();
                      return;
                    }
                    e.stopPropagation();
                    onSessionUnlinkClick();
                  }}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <TypographyCaption>
                <FormattedMessage
                  defaultMessage="Unlink session from patient"
                  id="7q0zwtrspq"
                  description="Tooltip for the action that removes a session from a patient profile"
                />
              </TypographyCaption>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

LinkedSession.displayName = 'LinkedSession';
export default LinkedSession;
